import { Autocomplete } from '@dev-spendesk/grapes';
import debounce from 'lodash/debounce';
import { useCallback, useState } from 'react';

import { baseAPI, frontAPI } from 'src/core/api/axios';
import { type Language } from 'src/core/config/i18n';
import { type GeoPlacesQueryType } from 'src/core/modules/requests/hooks/useGeoPlacesLastQuery';
import { logger } from 'src/utils/datadog-log-wrapper';

import { type Location } from './location';

type Props = {
  onPlaceSelect: (place: Location | undefined) => void;
  placeholder: string;
  requesterCountryCode: string | undefined;
  types?: GeoPlacesQueryType[];
  initialInputValue?: string;
  usePublicEndpoint?: boolean;
  lastPlaces?: Location[];
  showClearSelectionButton?: boolean;
  language: Language;
};

type PlaceAutocompleteOption = {
  key: string;
  label: string;
  location?: Location;
};

const PlaceAutocomplete = ({
  onPlaceSelect,
  placeholder,
  requesterCountryCode,
  types,
  initialInputValue = '',
  usePublicEndpoint = false,
  lastPlaces,
  showClearSelectionButton = false,
  language,
}: Props) => {
  const [selectedValue, setSelectedValue] = useState<
    PlaceAutocompleteOption | undefined
  >(
    initialInputValue
      ? {
          key: initialInputValue,
          label: initialInputValue,
          location: undefined,
        }
      : undefined,
  );
  const [placeSuggestionsOptions, setPlaceSuggestionsOptions] = useState<
    PlaceAutocompleteOption[]
  >(
    lastPlaces
      ? lastPlaces.map((place, index) => ({
          key: `${place.description}-${index}`,
          label: place.description,
          location: place,
        }))
      : [],
  );

  const handleInputChange = async (searchText: string | undefined) => {
    if (!searchText || searchText.length < 3) {
      return;
    }

    try {
      const clientAPI = usePublicEndpoint ? frontAPI : baseAPI;
      const { data } = await clientAPI.get('/geo/places', {
        params: {
          searchText,
          requesterCountryCode,
          types,
          language,
        },
      });
      if (data.matchingPlaces) {
        setPlaceSuggestionsOptions(
          data.matchingPlaces.map((location: Location) => ({
            key: location.description,
            label: location.description,
            location,
          })),
        );
      }
    } catch (error) {
      logger.warn('could not get places from /geo/places API', {
        team: 'travel-and-expenses',
        scope: 'requests::panel::mileage::creation',
        error,
      });
    }
  };

  const debouncedOnChange = useCallback(debounce(handleInputChange, 500), []);

  return (
    <Autocomplete
      fit="parent"
      showClearSelectionButton={showClearSelectionButton}
      placeholder={placeholder}
      value={selectedValue}
      onSelect={(selectedOption: PlaceAutocompleteOption | undefined) => {
        setSelectedValue(selectedOption);
        onPlaceSelect(selectedOption?.location);
      }}
      onSearch={debouncedOnChange}
      options={placeSuggestionsOptions}
    />
  );
};

export default PlaceAutocomplete;
